import React, { useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import { Box, CircularProgress, Grid, Stack, TextField } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import {
  AppFormikDatePicker,
  AppFormikSelect,
  AppFormikTextArea,
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { useNavigate, useParams } from 'react-router-dom';
import { RiskRegisterScheme } from 'validators/RiskRegisterScheme';
import PageHeader from 'shared/components/PageHeader';
import { RiskRegisterService } from 'services/RiskRegisterService';
import { IRiskRegister } from '../List';
import { Utils } from 'shared/helpers';
import ScrollToError from 'shared/components/ScrollToError';

interface AddRegisterRiskProps {
  quickAdd?: boolean;
  onClose?: (reload: any) => void;
}
const AddRegisterRisk: React.FC<AddRegisterRiskProps> = (props) => {
  const { quickAdd, onClose } = props;
  const navigate = useNavigate();
  const { riskId } = useParams();
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [riskDetail, setRiskDetail] = useState<IRiskRegister>({
    riskTitle: '',
    riskDescription: '',
    affectedStakeholders: '',
    mitigationPlan: '',
    residualRiskOwner: '',
    dueDate: '',
    riskType: '',
    likelihood: '',
    impact: 'Medium',
    riskSource: '',
    mitigationStatus: '',
    residualRiskLevel: 'Low',
  });

  // API Calls
  const loadRiskDetail = (riskId: string): void => {
    setIsLoadingDetail(true);
    RiskRegisterService.detail(riskId)
      .then((response) => {
        const { data } = response.data;
        setRiskDetail(data);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        navigate(-1);
      })
      .finally(() => setIsLoadingDetail(false));
  };
  const createRecord = (payload: any, callback: any): void => {
    RiskRegisterService.create(payload)
      .then((response) => {
        callback(response);
      })
      .catch(() => {
        callback(false);
      });
  };
  const updateRecord = (payload: any, callback: any): void => {
    RiskRegisterService.update(`${riskId}`, payload)
      .then((response) => {
        callback(response);
      })
      .catch(() => {
        callback(false);
      });
  };

  // UI Actions
  const actionOnSubmit = (
    formValues: any,
    formik: FormikHelpers<any>
  ): void => {
    if (riskId) {
      updateRecord(formValues, (response: any) => {
        formik.setSubmitting(false);
        if (typeof response === 'object') {
          navigate(-1);
        }
      });
    } else {
      createRecord(formValues, (response: any) => {
        formik.setSubmitting(false);
        if (typeof response === 'object') {
          if (quickAdd) {
            onClose?.(response);
          } else {
            navigate(-1);
          }
        } else if (quickAdd) {
          onClose?.(false);
        }
      });
    }
  };

  // Hooks
  useEffect(() => {
    if (riskId) {
      loadRiskDetail(riskId);
    }
  }, [riskId]);

  // UI Elements
  return (
    <>
      {!quickAdd && (
        <PageHeader
          title={riskId ? 'Edit Risk' : 'Create New Risk'}
          description={
            riskId
              ? 'Update the risk details below.'
              : 'Add a new risk to the register.'
          }
        />
      )}
      <Box className="risk-management add-new-risk">
        <HeadTag title="Risk register" />
        {isLoadingDetail && (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        )}
        {(!isLoadingDetail || !riskId) && (
          <Formik
            initialValues={riskDetail}
            validationSchema={RiskRegisterScheme}
            validateOnMount
            enableReinitialize
            onSubmit={(formValues, formik) =>
              actionOnSubmit(formValues, formik)
            }
          >
            {({ handleSubmit, isSubmitting }) => (
              <Grid container spacing={2} className="add-register-wrapper">
                <ScrollToError />
                <Grid item xs={12}>
                  <AppFormikTextField
                    name="riskTitle"
                    placeholder="Enter Risk Title"
                    label="Risk Title*"
                    outerLabel
                  />
                </Grid>
                <Grid item xs={12}>
                  <AppFormikTextArea
                    name="riskDescription"
                    placeholder="Provide a detailed description of the risk"
                    label="Risk Description*"
                    outerLabel
                  />
                </Grid>
                <Grid item xs={12}>
                  <AppFormikTextField
                    name="affectedStakeholders"
                    placeholder="Comma-separated list: e.g. Customers, Employees, Partners"
                    label="Affected Stakeholders*"
                    outerLabel
                  />
                </Grid>
                <Grid item xs={12}>
                  <AppFormikTextArea
                    name="mitigationPlan"
                    placeholder="Describe the plan to mitigate this risk"
                    label="Mitigation Plan*"
                    outerLabel
                  />
                </Grid>
                {!quickAdd && (
                  <Grid item xs={12}>
                    <AppFormikSelect
                      name="residualRiskOwner"
                      className="rework-selection"
                      notched={false}
                      labelId="fix-selection-label"
                      testid="solicitor-dropdown"
                      MenuProps={{
                        className: 'menu-fix-selection single-fix-rework',
                      }}
                      outerLabel
                      placeholder="Select User"
                      notEmphasized
                      notSelectablePlaceholder
                      label="Residual Risk Owner (optional)"
                      items={['None', 'Chris Dubey']}
                    />
                  </Grid>
                )}
                {!quickAdd && (
                  <Grid item xs={12} sm={6}>
                    <AppFormikDatePicker
                      autoFocus
                      fullWidth
                      className="mb-1 mt-2"
                      name="dueDate"
                      testid="datepicker-dropdown"
                      InputProps={{
                        name: 'dueDate',
                        'aria-labelledby': 'duedate-label',
                        notched: false,
                      }}
                      value={undefined}
                      onChange={() => console.log('ripple')}
                      onChangeDate={() => console.log('ripple')}
                      onAccepted={() => {
                        console.log('ripple');
                      }}
                      onClose={() => {
                        console.log('ripple');
                      }}
                      label="Due Date (optional)"
                      outerLabel
                      placeholder="gg/mm/aaaa"
                      renderInput={(props: any) => <TextField {...props} />}
                      autoComplete="off"
                      onEnter={() => {
                        console.log('ripple');
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <AppFormikSelect
                    name="riskType"
                    className="rework-selection mt-2"
                    notched={false}
                    labelId="fix-selection-label"
                    testid="solicitor-dropdown"
                    MenuProps={{
                      className: 'menu-fix-selection single-fix-rework',
                    }}
                    outerLabel
                    placeholder="Select Risk Type"
                    notEmphasized
                    notSelectablePlaceholder
                    label="Risk Type*"
                    items={[
                      'Model Bias',
                      'Data Privacy',
                      'Adversarial Vulnerability',
                      'Explainability Gap',
                      'Regulatory Risk',
                      'Reputational Risk',
                      'Physical Safety',
                      'Legal Liabilities',
                      'Regulatory Compliance',
                      'Negative Impact on Individuals, Groups or Society',
                    ]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <AppFormikSelect
                    name="likelihood"
                    className="rework-selection"
                    notched={false}
                    labelId="fix-selection-label"
                    testid="solicitor-dropdown"
                    MenuProps={{
                      className: 'menu-fix-selection single-fix-rework',
                    }}
                    outerLabel
                    placeholder="Select Likelihood"
                    notEmphasized
                    notSelectablePlaceholder
                    label="Likelihood*"
                    items={[
                      'Rare',
                      'Unlikely',
                      'Possible',
                      'Likely',
                      'Almost Certain',
                    ]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <AppFormikSelect
                    name="impact"
                    className="rework-selection"
                    notched={false}
                    labelId="fix-selection-label"
                    testid="solicitor-dropdown"
                    MenuProps={{
                      className: 'menu-fix-selection single-fix-rework',
                    }}
                    outerLabel
                    placeholder="Select Impact"
                    notEmphasized
                    notSelectablePlaceholder
                    label="Impact*"
                    items={['Low', 'Moderate', 'High', 'Critical']}
                  />
                </Grid>
                {!quickAdd && (
                  <Grid item xs={12} sm={6}>
                    <AppFormikSelect
                      name="riskSource"
                      className="rework-selection"
                      notched={false}
                      labelId="fix-selection-label"
                      testid="solicitor-dropdown"
                      MenuProps={{
                        className: 'menu-fix-selection single-fix-rework',
                      }}
                      outerLabel
                      placeholder="Select Risk Source"
                      notEmphasized
                      notSelectablePlaceholder
                      label="Risk Source"
                      items={[
                        'Dataset',
                        'Manual Entry',
                        'Audit',
                        'Automated Scan',
                        'Stakeholder Feedback',
                        'External Disclosure',
                      ]}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <AppFormikSelect
                    name="mitigationStatus"
                    className="rework-selection"
                    notched={false}
                    labelId="fix-selection-label"
                    testid="solicitor-dropdown"
                    MenuProps={{
                      className: 'menu-fix-selection single-fix-rework',
                    }}
                    outerLabel
                    placeholder="Select Status"
                    notEmphasized
                    notSelectablePlaceholder
                    label="Mitigation Status*"
                    items={[
                      'Not Started',
                      'In Progress',
                      'Mitigated',
                      'Accepted',
                      'Archived',
                    ]}
                  />
                </Grid>
                <Grid item xs={12}>
                  <AppFormikSelect
                    name="residualRiskLevel"
                    className="rework-selection"
                    notched={false}
                    labelId="fix-selection-label"
                    testid="solicitor-dropdown"
                    MenuProps={{
                      className: 'menu-fix-selection single-fix-rework',
                    }}
                    outerLabel
                    placeholder="Select Level"
                    notEmphasized
                    notSelectablePlaceholder
                    label="Residual Risk Level"
                    items={['Low', 'Moderate', 'High', 'Critical']}
                  />
                </Grid>
                <Grid item xs={12} justifyContent="flex-end">
                  <Stack
                    spacing={4}
                    direction={{ sx: 'column-reverse', sm: 'row-reverse' }}
                    className="mt-4"
                    gap={2}
                  >
                    <AppPrimaryButton
                      isLoading={isSubmitting}
                      disabled={isSubmitting}
                      title={riskId ? 'Update Risk' : 'Create Risk'}
                      onClick={handleSubmit}
                    />
                    <AppPrimaryButtonOutlined
                      title="Cancel"
                      disabled={isSubmitting}
                      onClick={() => {
                        if (quickAdd) {
                          onClose?.(false);
                        } else {
                          navigate('/risk-register');
                        }
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            )}
          </Formik>
        )}
      </Box>
    </>
  );
};

export default AddRegisterRisk;
